import { useState } from "react";
import { Link, createRoute, useParams } from "@tanstack/react-router";
import { ApiError } from "@plotlounge/shared/api";
import { getApiClient } from "@/lib/api-client";
import { rootRoute } from "./root";
import styles from "./rsvp-cancel.module.scss";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "cancelled" }
  | { kind: "invalid" }
  | { kind: "error"; message: string };

function RsvpCancelRoute() {
  const params = useParams({ from: rsvpCancelRoute.id });
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function handleCancel() {
    if (status.kind === "submitting") return;
    setStatus({ kind: "submitting" });
    try {
      await getApiClient().post(`/public/rsvp/${params.token}/cancel`);
      setStatus({ kind: "cancelled" });
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 401)
      ) {
        setStatus({ kind: "invalid" });
        return;
      }
      const message =
        err instanceof ApiError && err.status >= 500
          ? "Something went wrong on our end. Please try again."
          : err instanceof ApiError
            ? err.message || "Couldn't cancel."
            : "Couldn't cancel. Please try again.";
      setStatus({ kind: "error", message });
    }
  }

  return (
    <main className={styles.root}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          Plotlounge
        </Link>
      </header>

      <section className={styles.card}>
        {status.kind === "cancelled" ? (
          <>
            <h1 className={styles.title}>Cancelled</h1>
            <p className={styles.body}>
              Your RSVP has been cancelled. Thanks for letting us know.
            </p>
            <Link to="/discover" className={styles.cta}>
              Find another gathering →
            </Link>
          </>
        ) : status.kind === "invalid" ? (
          <>
            <h1 className={styles.title}>This link can't be used</h1>
            <p className={styles.body}>
              This cancel link is invalid or expired. If you can't find your
              confirmation, reply to it instead.
            </p>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Cancel your RSVP?</h1>
            <p className={styles.body}>
              No login needed — clicking confirm releases your seat for someone
              else. You can RSVP again any time.
            </p>
            <button
              type="button"
              onClick={handleCancel}
              className={styles.cta}
              disabled={status.kind === "submitting"}
            >
              {status.kind === "submitting" ? "Cancelling…" : "Cancel my RSVP"}
            </button>
            {status.kind === "error" && (
              <p className={styles.error} role="alert">
                {status.message}
              </p>
            )}
          </>
        )}
      </section>
    </main>
  );
}

export const rsvpCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rsvp/cancel/$token",
  component: RsvpCancelRoute,
});

export { RsvpCancelRoute };
